import React, { useEffect, useState, useCallback } from "react";
import { cartApi } from "../services/api";
import "./Page.css";

const STATUS_CONFIG = {
  running_low: { label: "Running Low", color: "#f59e0b", emoji: "🟡" },
  finished: { label: "Finished", color: "#ef4444", emoji: "🔴" },
  enough: { label: "Enough", color: "#22c55e", emoji: "🟢" },
};

export default function CartPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const data = await cartApi.getCart();
      setItems(data);
      setError(null);
    } catch {
      setError("Failed to load cart.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const handleRemove = async (id) => {
    try {
      await cartApi.removeItem(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch {
      setError("Failed to remove item.");
    }
  };

  const handlePurchased = async (id) => {
    try {
      await cartApi.markPurchased(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setMessage("✅ Marked as purchased!");
      setTimeout(() => setMessage(null), 3000);
    } catch {
      setError("Failed to mark as purchased.");
    }
  };

  const handleSend = async () => {
    setSending(true);
    setMessage(null);
    setError(null);
    try {
      const result = await cartApi.sendCart();
      setMessage(`✅ ${result.message}`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to send cart.";
      setError(`❌ ${detail}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">🛒 Shopping Cart</h1>
          <p className="page-sub">{items.length} item{items.length !== 1 ? "s" : ""} to buy</p>
        </div>
        {items.length > 0 && (
          <button className="btn-send" onClick={handleSend} disabled={sending}>
            {sending ? "Sending…" : "📨 Send to Telegram"}
          </button>
        )}
      </div>

      {message && <div className="success-banner">{message}</div>}
      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="loading">Loading cart…</div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎉</div>
          <p>Cart is empty — all items are stocked!</p>
        </div>
      ) : (
        <div className="cart-list">
          {items.map((item) => {
            const cfg = STATUS_CONFIG[item.status_snapshot] || {};
            return (
              <div key={item.id} className="cart-row">
                <div className="cart-item-info">
                  <div className="cart-item-top">
                    <span className="cart-item-name">{item.item_name}</span>
                    {item.quantity > 1 && (
                      <span className="cart-qty">×{item.quantity}</span>
                    )}
                  </div>
                  <span className="cart-badge" style={{ color: cfg.color }}>
                    {cfg.emoji} {cfg.label}
                  </span>
                </div>
                <div className="cart-actions">
                  <button className="btn-purchased" onClick={() => handlePurchased(item.id)} title="Mark as purchased">
                    ✅
                  </button>
                  <button className="btn-remove" onClick={() => handleRemove(item.id)} title="Remove from cart">
                    ✕
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
